import * as THREE from 'three';
import type { SolidBodyDisplayMaterialRecipe } from './solid-body-display-material-recipe';
import { SolidBodyMeshMaterialFactory } from './SolidBodyMeshMaterialFactory';

type ResultMaterialOptions = {
  /**
   * 当前 Viewer 场景。
   *
   * 设计说明：保留该参数是为了兼容既有调用方；正式特征结果体当前不再继承导入件 PBR，避免新建特征过亮或过反光。
   */
  scene?: THREE.Scene;
};

/** 特征替换预览体固定基色。 */
export const FEATURE_PREVIEW_MESH_COLOR = 0xffff00;

/** 特征替换预览体固定透明度。 */
export const FEATURE_PREVIEW_MESH_OPACITY = 0.4;


/** 正式特征结果体固定基色。 */
export const FEATURE_RESULT_MESH_COLOR = 0x9dcfed;

/** 正式特征结果体固定金属度。 */
export const FEATURE_RESULT_MESH_METALNESS = 0.8;

/** 正式特征结果体固定粗糙度。 */
export const FEATURE_RESULT_MESH_ROUGHNESS = 0.8;

/**
 * 创建正式特征结果体材质配方。
 *
 * 设计说明：
 * 1. 正式建模特征的结果体颜色固定为产品当前指定的 `#9dcfed`；
 * 2. 金属度与粗糙度固定为产品当前指定值，避免导入件 PBR 影响新建特征外观；
 * 3. 该函数集中输出正式结果体主材质配方，避免拉伸、旋转、圆角等功能重复散落材质参数。
 *
 * @returns 正式特征结果体材质配方。
 */
function createFeatureResultMaterialRecipe(): SolidBodyDisplayMaterialRecipe {
  return {
    // 颜色固定，确保所有新建正式特征结果体具备一致的实体识别色。
    color: FEATURE_RESULT_MESH_COLOR,
    // 正式特征结果体使用固定 PBR 参数，确保新建 mesh 外观一致。
    metalness: FEATURE_RESULT_MESH_METALNESS,
    roughness: FEATURE_RESULT_MESH_ROUGHNESS,
  };
}


/**
 * 特征实体面材质工厂。
 *
 * 文件定位：
 * 1. 当前文件位于 cad-core 的 material 目录，属于材质封装层；
 * 2. 它统一创建拉伸/旋转等三维特征结果体与预览体的主材质；
 * 3. 通过独立工厂收口结果体颜色与 `side=DoubleSide` 语义，避免 MeshManager、状态机或后续功能各自重复创建并产生偏差。
 */
export class FeatureMeshMaterialFactory {
  /**
   * 创建预览实体主材质。
   *
   * @returns 预览材质。
   */
  public static createPreviewMaterial(): THREE.MeshStandardMaterial {
    return new THREE.MeshStandardMaterial({
      color: FEATURE_PREVIEW_MESH_COLOR,
      transparent: true,
      opacity: FEATURE_PREVIEW_MESH_OPACITY,
      metalness: 0.5,
      roughness: 0.5,
      depthTest: true,
      depthWrite: false,
      side: THREE.DoubleSide,
    });
  }

  /**
   * 创建正式结果实体主材质。
   *
   * @param options 结果材质选项。
   * @returns 结果材质。
   */
  public static createResultMaterial(options?: ResultMaterialOptions): THREE.MeshStandardMaterial {
    // 当前保留 options 只为兼容旧调用签名，正式结果体 PBR 不再从 scene 采样。
    void options;
    const resultRecipe = createFeatureResultMaterialRecipe();

    return SolidBodyMeshMaterialFactory.createSolidBodyMaterial(resultRecipe);
  }
}
